import React, { useState, useEffect } from 'react';
import { 
  getItineraries, 
  createItinerary, 
  deleteItinerary, 
  getCustomers,
  getBookings,
  downloadItineraryPdf,
  downloadItineraryDocx
} from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Plus, 
  MoreVertical, 
  Trash2, 
  Download, 
  FileText, 
  FileType,
  MapPin,
  Calendar,
  Users,
  Loader2,
  X,
  Clock,
  Copy,
  MessageSquare,
  Bookmark,
  BookTemplate,
  Send,
  Eye,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

// Itinerary Templates
const ITINERARY_TEMPLATES = [
  {
    id: 'goa-4d',
    name: 'Goa Beach Getaway - 4 Days',
    destination: 'Goa',
    days: [
      { day_number: 1, title: 'Arrival & Beach Relaxation', activities: [
        { time: '10:00', activity: 'Airport Pickup', description: 'Welcome & transfer to hotel', location: 'Goa Airport' },
        { time: '14:00', activity: 'Check-in & Lunch', description: 'Settle in and enjoy lunch', location: 'Hotel' },
        { time: '16:00', activity: 'Beach Visit', description: 'Relax at Calangute Beach', location: 'Calangute Beach' }
      ]},
      { day_number: 2, title: 'North Goa Sightseeing', activities: [
        { time: '09:00', activity: 'Breakfast', description: 'Breakfast at hotel', location: 'Hotel' },
        { time: '10:00', activity: 'Fort Aguada', description: 'Visit the historic fort', location: 'Fort Aguada' },
        { time: '14:00', activity: 'Baga Beach', description: 'Water sports & beach activities', location: 'Baga Beach' }
      ]},
      { day_number: 3, title: 'South Goa & Cruise', activities: [
        { time: '09:00', activity: 'South Goa Tour', description: 'Visit churches and temples', location: 'Old Goa' },
        { time: '18:00', activity: 'Sunset Cruise', description: 'Mandovi river cruise with dinner', location: 'Mandovi River' }
      ]},
      { day_number: 4, title: 'Departure', activities: [
        { time: '08:00', activity: 'Breakfast & Checkout', description: 'Pack and checkout', location: 'Hotel' },
        { time: '11:00', activity: 'Airport Drop', description: 'Transfer to airport', location: 'Goa Airport' }
      ]}
    ],
    inclusions: ['Accommodation (3 nights)', 'Daily breakfast', 'Airport transfers', 'Sightseeing as per itinerary', 'Sunset cruise with dinner'],
    exclusions: ['Airfare', 'Lunch & dinner (except cruise)', 'Personal expenses', 'Water sports'],
    hotel_details: 'Deluxe Beach Resort - Sea View Rooms',
    terms: ['50% advance at booking', 'Balance 7 days before travel', 'Cancellation charges apply']
  },
  {
    id: 'kerala-7d',
    name: 'Kerala Backwaters - 7 Days',
    destination: 'Kerala',
    days: [
      { day_number: 1, title: 'Cochin Arrival', activities: [
        { time: '12:00', activity: 'Airport Pickup', description: 'Welcome to Kerala', location: 'Cochin Airport' },
        { time: '15:00', activity: 'Fort Kochi Tour', description: 'Chinese fishing nets, St. Francis Church', location: 'Fort Kochi' }
      ]},
      { day_number: 2, title: 'Cochin to Munnar', activities: [
        { time: '08:00', activity: 'Drive to Munnar', description: '4 hour scenic drive through tea estates', location: 'Munnar' },
        { time: '14:00', activity: 'Tea Museum', description: 'Learn about tea processing', location: 'Tea Museum' }
      ]},
      { day_number: 3, title: 'Munnar Sightseeing', activities: [
        { time: '09:00', activity: 'Eravikulam National Park', description: 'Spot Nilgiri Tahr', location: 'Eravikulam' },
        { time: '14:00', activity: 'Mattupetty Dam', description: 'Boating and photo stop', location: 'Mattupetty' }
      ]},
      { day_number: 4, title: 'Munnar to Thekkady', activities: [
        { time: '09:00', activity: 'Drive to Thekkady', description: '3 hour drive', location: 'Thekkady' },
        { time: '15:00', activity: 'Spice Plantation', description: 'Guided tour of spice gardens', location: 'Spice Garden' }
      ]},
      { day_number: 5, title: 'Thekkady to Alleppey', activities: [
        { time: '08:00', activity: 'Periyar Lake', description: 'Optional boat safari', location: 'Periyar' },
        { time: '12:00', activity: 'Drive to Alleppey', description: 'Check-in to houseboat', location: 'Alleppey' }
      ]},
      { day_number: 6, title: 'Houseboat Cruise', activities: [
        { time: 'All Day', activity: 'Backwater Cruise', description: 'Overnight stay on houseboat', location: 'Alleppey Backwaters' }
      ]},
      { day_number: 7, title: 'Departure', activities: [
        { time: '09:00', activity: 'Disembark', description: 'Leave houseboat', location: 'Alleppey' },
        { time: '13:00', activity: 'Airport Transfer', description: 'Transfer to Cochin airport', location: 'Cochin Airport' }
      ]}
    ],
    inclusions: ['Accommodation (6 nights)', 'Daily breakfast', 'All transfers by AC vehicle', 'Houseboat with all meals', 'Sightseeing as per itinerary'],
    exclusions: ['Airfare', 'Lunch & dinner (except houseboat)', 'Entry tickets', 'Boating at Periyar'],
    hotel_details: 'Premium Hotels + 1 Night Houseboat',
    terms: ['50% advance at booking', 'Balance before departure', 'Non-refundable in peak season']
  },
  {
    id: 'rajasthan-5d',
    name: 'Rajasthan Royal Tour - 5 Days',
    destination: 'Rajasthan',
    days: [
      { day_number: 1, title: 'Jaipur - Pink City', activities: [
        { time: '09:00', activity: 'Amber Fort', description: 'Elephant ride to fort', location: 'Amber Fort' },
        { time: '14:00', activity: 'City Palace', description: 'Royal heritage tour', location: 'City Palace' },
        { time: '17:00', activity: 'Hawa Mahal', description: 'Photo stop at Palace of Winds', location: 'Hawa Mahal' }
      ]},
      { day_number: 2, title: 'Jaipur to Jodhpur', activities: [
        { time: '08:00', activity: 'Drive to Jodhpur', description: '5 hour drive via Ajmer', location: 'Jodhpur' },
        { time: '16:00', activity: 'Mehrangarh Fort', description: 'Sunset at the majestic fort', location: 'Mehrangarh' }
      ]},
      { day_number: 3, title: 'Jodhpur to Udaipur', activities: [
        { time: '08:00', activity: 'Drive to Udaipur', description: '5 hour drive via Ranakpur', location: 'Udaipur' },
        { time: '12:00', activity: 'Ranakpur Jain Temple', description: 'Stunning marble architecture', location: 'Ranakpur' }
      ]},
      { day_number: 4, title: 'Udaipur - City of Lakes', activities: [
        { time: '09:00', activity: 'City Palace', description: 'Largest palace in Rajasthan', location: 'City Palace' },
        { time: '14:00', activity: 'Boat Ride', description: 'Lake Pichola boat ride', location: 'Lake Pichola' },
        { time: '19:00', activity: 'Cultural Show', description: 'Bagore Ki Haveli', location: 'Bagore Ki Haveli' }
      ]},
      { day_number: 5, title: 'Departure', activities: [
        { time: '08:00', activity: 'Local Market', description: 'Shopping for handicrafts', location: 'Hathi Pol' },
        { time: '12:00', activity: 'Airport Transfer', description: 'Departure', location: 'Udaipur Airport' }
      ]}
    ],
    inclusions: ['4 nights accommodation', 'Daily breakfast', 'AC vehicle for transfers', 'Sightseeing as per itinerary', 'Elephant ride at Amber'],
    exclusions: ['Airfare', 'Lunch & dinner', 'Entry tickets', 'Camera fees'],
    hotel_details: 'Heritage Hotels & Havelis',
    terms: ['40% advance required', 'Balance 10 days before travel']
  }
];

export const Itineraries = () => {
  const [itineraries, setItineraries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [itineraryType, setItineraryType] = useState('daywise'); // text, daywise, template
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewItinerary, setPreviewItinerary] = useState(null);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    customer_id: '',
    booking_id: '',
    destination: '',
    travel_date: '',
    return_date: '',
    travelers: 1,
    days: [],
    hotel_details: '',
    flight_details: '',
    inclusions: [],
    exclusions: [],
    terms: [],
    emergency_contact: '',
    notes: '',
    itinerary_type: 'daywise',
    text_content: '',
    cost_per_person: '',
  });

  const [newInclusion, setNewInclusion] = useState('');
  const [newExclusion, setNewExclusion] = useState('');
  const [newTerm, setNewTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [itinerariesRes, customersRes, bookingsRes] = await Promise.all([
        getItineraries(),
        getCustomers(),
        getBookings(),
      ]);
      setItineraries(itinerariesRes.data);
      setCustomers(customersRes.data);
      setBookings(bookingsRes.data);
      
      // Load saved templates
      try {
        const templatesRes = await api.get('/itinerary-templates');
        setTemplates([...ITINERARY_TEMPLATES, ...(templatesRes.data || [])]);
      } catch {
        setTemplates(ITINERARY_TEMPLATES);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      customer_id: '',
      booking_id: '',
      destination: '',
      travel_date: '',
      return_date: '',
      travelers: 1,
      days: [],
      hotel_details: '',
      flight_details: '',
      inclusions: [],
      exclusions: [],
      terms: [],
      emergency_contact: '',
      notes: '',
      itinerary_type: 'daywise',
      text_content: '',
      cost_per_person: '',
    });
    setNewInclusion('');
    setNewExclusion('');
    setNewTerm('');
    setSelectedTemplate(null);
    setItineraryType('daywise');
  };

  const handleBookingSelect = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (booking) {
      setFormData({
        ...formData,
        booking_id: bookingId,
        customer_id: booking.customer_id,
        destination: booking.destination,
        travel_date: booking.travel_date,
        return_date: booking.return_date,
        travelers: booking.travelers || 1,
        title: `${booking.destination} Tour Package`,
      });
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setFormData({
        ...formData,
        title: template.name,
        destination: template.destination,
        days: JSON.parse(JSON.stringify(template.days)),
        inclusions: [...template.inclusions],
        exclusions: [...template.exclusions],
        hotel_details: template.hotel_details || '',
        terms: template.terms || [],
        itinerary_type: 'template',
      });
    }
  };

  const addDay = () => {
    const newDayNumber = formData.days.length + 1;
    setFormData({
      ...formData,
      days: [...formData.days, {
        day_number: newDayNumber,
        date: '',
        title: `Day ${newDayNumber}`,
        meal_plan: '',
        activities: []
      }]
    });
  };

  const updateDay = (index, field, value) => {
    const updatedDays = [...formData.days];
    updatedDays[index] = { ...updatedDays[index], [field]: value };
    setFormData({ ...formData, days: updatedDays });
  };

  const removeDay = (index) => {
    const updatedDays = formData.days.filter((_, i) => i !== index);
    updatedDays.forEach((day, i) => {
      day.day_number = i + 1;
    });
    setFormData({ ...formData, days: updatedDays });
  };

  const addActivity = (dayIndex) => {
    const updatedDays = [...formData.days];
    updatedDays[dayIndex].activities.push({
      time: '',
      activity: '',
      description: '',
      location: ''
    });
    setFormData({ ...formData, days: updatedDays });
  };

  const updateActivity = (dayIndex, activityIndex, field, value) => {
    const updatedDays = [...formData.days];
    updatedDays[dayIndex].activities[activityIndex][field] = value;
    setFormData({ ...formData, days: updatedDays });
  };

  const removeActivity = (dayIndex, activityIndex) => {
    const updatedDays = [...formData.days];
    updatedDays[dayIndex].activities = updatedDays[dayIndex].activities.filter((_, i) => i !== activityIndex);
    setFormData({ ...formData, days: updatedDays });
  };

  const addInclusion = () => {
    if (newInclusion.trim()) {
      setFormData({ ...formData, inclusions: [...formData.inclusions, newInclusion.trim()] });
      setNewInclusion('');
    }
  };

  const removeInclusion = (index) => {
    setFormData({ ...formData, inclusions: formData.inclusions.filter((_, i) => i !== index) });
  };

  const addExclusion = () => {
    if (newExclusion.trim()) {
      setFormData({ ...formData, exclusions: [...formData.exclusions, newExclusion.trim()] });
      setNewExclusion('');
    }
  };

  const removeExclusion = (index) => {
    setFormData({ ...formData, exclusions: formData.exclusions.filter((_, i) => i !== index) });
  };

  const addTerm = () => {
    if (newTerm.trim()) {
      setFormData({ ...formData, terms: [...formData.terms, newTerm.trim()] });
      setNewTerm('');
    }
  };

  const removeTerm = (index) => {
    setFormData({ ...formData, terms: formData.terms.filter((_, i) => i !== index) });
  };

  // Generate Text Itinerary for WhatsApp
  const generateTextItinerary = (data = formData) => {
    const customerName = customers.find(c => c.id === data.customer_id)?.name || '';
    const numDays = data.days?.length || Math.ceil((new Date(data.return_date) - new Date(data.travel_date)) / (1000 * 60 * 60 * 24)) + 1;
    
    let text = `🌴 *${data.title || data.destination + ' Tour'}*\n`;
    text += `━━━━━━━━━━━━━━━━━━\n\n`;
    
    if (customerName) text += `👤 Dear ${customerName},\n\n`;
    
    text += `📍 *Destination:* ${data.destination}\n`;
    text += `📅 *Dates:* ${formatDate(data.travel_date)} - ${formatDate(data.return_date)}\n`;
    text += `⏱️ *Duration:* ${numDays} Days / ${numDays - 1} Nights\n`;
    text += `👥 *Travelers:* ${data.travelers}\n\n`;
    
    if (data.days && data.days.length > 0) {
      text += `📋 *ITINERARY*\n`;
      text += `━━━━━━━━━━━━━━━━━━\n`;
      data.days.forEach(day => {
        text += `\n*Day ${day.day_number}: ${day.title}*\n`;
        if (day.activities && day.activities.length > 0) {
          day.activities.forEach(act => {
            text += `  ▸ ${act.time ? act.time + ' - ' : ''}${act.activity}\n`;
          });
        }
      });
      text += `\n`;
    }
    
    if (data.hotel_details) {
      text += `🏨 *Hotel:* ${data.hotel_details}\n`;
    }
    if (data.flight_details) {
      text += `✈️ *Flight:* ${data.flight_details}\n`;
    }
    
    if (data.inclusions && data.inclusions.length > 0) {
      text += `\n✅ *INCLUSIONS*\n`;
      data.inclusions.forEach(inc => {
        text += `  • ${inc}\n`;
      });
    }
    
    if (data.exclusions && data.exclusions.length > 0) {
      text += `\n❌ *EXCLUSIONS*\n`;
      data.exclusions.forEach(exc => {
        text += `  • ${exc}\n`;
      });
    }
    
    if (data.cost_per_person) {
      text += `\n💰 *Cost:* ₹${parseInt(data.cost_per_person).toLocaleString('en-IN')} per person\n`;
    }
    
    text += `\n━━━━━━━━━━━━━━━━━━\n`;
    text += `🦅 *KITES FLY* - The Travel Companion\n`;
    text += `📞 Contact us for bookings!\n`;
    
    return text;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard! Ready to paste in WhatsApp');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.destination || !formData.travel_date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    // For text type, generate the text content
    const submitData = {
      ...formData,
      itinerary_type: itineraryType,
      text_content: itineraryType === 'text' ? generateTextItinerary() : formData.text_content,
    };
    
    setSubmitting(true);
    try {
      await createItinerary(submitData);
      toast.success('Itinerary created successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create itinerary');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!formData.title || !formData.destination || formData.days.length === 0) {
      toast.error('Please fill title, destination and at least one day');
      return;
    }
    try {
      await api.post('/itinerary-templates', {
        name: formData.title,
        destination: formData.destination,
        days: formData.days,
        inclusions: formData.inclusions,
        exclusions: formData.exclusions,
        hotel_details: formData.hotel_details,
        terms: formData.terms,
      });
      toast.success('Template saved successfully!');
      loadData();
    } catch {
      toast.error('Failed to save template');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this itinerary?')) return;
    try {
      await deleteItinerary(id);
      toast.success('Itinerary deleted');
      loadData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleDownloadPdf = async (id, destination) => {
    try {
      const response = await downloadItineraryPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Itinerary_${destination}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const handleDownloadDocx = async (id, destination) => {
    try {
      const response = await downloadItineraryDocx(id);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Itinerary_${destination}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Word document downloaded');
    } catch {
      toast.error('Failed to download document');
    }
  };

  const handlePreview = (itinerary) => {
    setPreviewItinerary(itinerary);
    setPreviewOpen(true);
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'text': return <Badge variant="secondary" className="bg-green-100 text-green-700">Text</Badge>;
      case 'template': return <Badge variant="secondary" className="bg-purple-100 text-purple-700">Template</Badge>;
      default: return <Badge variant="secondary" className="bg-blue-100 text-blue-700">Day-wise</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="itineraries-container" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>Tour Itineraries</h1>
          <p className="text-muted-foreground mt-1">Create text, day-wise, or template-based itineraries</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-itinerary-btn"
              onClick={() => { resetForm(); setDialogOpen(true); }} 
              className="text-white" 
              style={{ backgroundColor: '#383838' }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Itinerary
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit text-xl" style={{ color: '#84bd0e' }}>Create Tour Itinerary</DialogTitle>
            </DialogHeader>
            
            {/* Itinerary Type Selector */}
            <div className="mt-4 mb-6">
              <Label className="mb-3 block font-semibold">Select Itinerary Type</Label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setItineraryType('text')}
                  className={`p-4 rounded-lg border-2 transition-all ${itineraryType === 'text' ? 'border-[#383838] bg-[#383838]/10' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <MessageSquare className={`w-6 h-6 mx-auto mb-2 ${itineraryType === 'text' ? 'text-[#383838]' : 'text-gray-400'}`} />
                  <div className="font-medium text-sm">Text Itinerary</div>
                  <div className="text-xs text-muted-foreground mt-1">WhatsApp friendly</div>
                </button>
                <button
                  type="button"
                  onClick={() => setItineraryType('daywise')}
                  className={`p-4 rounded-lg border-2 transition-all ${itineraryType === 'daywise' ? 'border-[#383838] bg-[#383838]/10' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <Calendar className={`w-6 h-6 mx-auto mb-2 ${itineraryType === 'daywise' ? 'text-[#383838]' : 'text-gray-400'}`} />
                  <div className="font-medium text-sm">Day-by-Day</div>
                  <div className="text-xs text-muted-foreground mt-1">Detailed activities</div>
                </button>
                <button
                  type="button"
                  onClick={() => setItineraryType('template')}
                  className={`p-4 rounded-lg border-2 transition-all ${itineraryType === 'template' ? 'border-[#383838] bg-[#383838]/10' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <BookTemplate className={`w-6 h-6 mx-auto mb-2 ${itineraryType === 'template' ? 'text-[#383838]' : 'text-gray-400'}`} />
                  <div className="font-medium text-sm">From Template</div>
                  <div className="text-xs text-muted-foreground mt-1">Quick start</div>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Template Selector (only for template type) */}
              {itineraryType === 'template' && (
                <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
                  <Label className="font-semibold">Select a Template</Label>
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                    {templates.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleTemplateSelect(t.id)}
                        className={`p-3 text-left rounded-lg border transition-all ${selectedTemplate?.id === t.id ? 'border-[#84bd0e] bg-white shadow-sm' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                      >
                        <div className="font-medium">{t.name}</div>
                        <div className="text-xs text-muted-foreground">{t.destination} • {t.days?.length} Days</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm uppercase tracking-wider" style={{ color: '#383838' }}>Basic Information</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Select Booking (Optional)</Label>
                    <Select value={formData.booking_id} onValueChange={handleBookingSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Auto-fill from booking" />
                      </SelectTrigger>
                      <SelectContent>
                        {bookings.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.destination} - {getCustomerName(b.customer_id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Customer *</Label>
                    <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Itinerary Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., Maldives Honeymoon Package"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Destination *</Label>
                    <Input
                      value={formData.destination}
                      onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                      placeholder="e.g., Maldives"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Travel Date *</Label>
                    <Input
                      type="date"
                      value={formData.travel_date}
                      onChange={(e) => setFormData({ ...formData, travel_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Return Date</Label>
                    <Input
                      type="date"
                      value={formData.return_date}
                      onChange={(e) => setFormData({ ...formData, return_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Travelers</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.travelers}
                      onChange={(e) => setFormData({ ...formData, travelers: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cost/Person (₹)</Label>
                    <Input
                      type="number"
                      value={formData.cost_per_person}
                      onChange={(e) => setFormData({ ...formData, cost_per_person: e.target.value })}
                      placeholder="e.g., 50000"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hotel Details</Label>
                    <Input
                      value={formData.hotel_details}
                      onChange={(e) => setFormData({ ...formData, hotel_details: e.target.value })}
                      placeholder="e.g., 5-Star Beach Resort"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Flight Details</Label>
                    <Input
                      value={formData.flight_details}
                      onChange={(e) => setFormData({ ...formData, flight_details: e.target.value })}
                      placeholder="e.g., IndiGo 6E-1234"
                    />
                  </div>
                </div>
              </div>

              {/* Day-by-Day Section (for daywise and template types) */}
              {(itineraryType === 'daywise' || itineraryType === 'template') && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm uppercase tracking-wider" style={{ color: '#383838' }}>Day-by-Day Itinerary</h3>
                    <Button type="button" variant="outline" size="sm" onClick={addDay}>
                      <Plus className="w-4 h-4 mr-1" /> Add Day
                    </Button>
                  </div>
                  
                  {formData.days.length === 0 ? (
                    <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed">
                      <Calendar className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                      <p className="text-muted-foreground">No days added yet. Click "Add Day" to start.</p>
                    </div>
                  ) : (
                    <Accordion type="multiple" className="space-y-2">
                      {formData.days.map((day, dayIndex) => (
                        <AccordionItem key={dayIndex} value={`day-${dayIndex}`} className="border rounded-lg px-4">
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-3">
                              <Badge style={{ backgroundColor: '#84bd0e' }} className="text-white">Day {day.day_number}</Badge>
                              <span className="font-medium">{day.title}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="pt-4 space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                              <div className="space-y-2">
                                <Label>Day Title</Label>
                                <Input
                                  value={day.title}
                                  onChange={(e) => updateDay(dayIndex, 'title', e.target.value)}
                                  placeholder="e.g., Arrival & City Tour"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Date (Optional)</Label>
                                <Input
                                  type="date"
                                  value={day.date || ''}
                                  onChange={(e) => updateDay(dayIndex, 'date', e.target.value)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Meal Plan</Label>
                                <Select value={day.meal_plan || ''} onValueChange={(v) => updateDay(dayIndex, 'meal_plan', v)}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select meals" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="NO MEALS">No Meals</SelectItem>
                                    <SelectItem value="BREAKFAST">Breakfast</SelectItem>
                                    <SelectItem value="BREAKFAST, LUNCH">Breakfast, Lunch</SelectItem>
                                    <SelectItem value="BREAKFAST, DINNER">Breakfast, Dinner</SelectItem>
                                    <SelectItem value="LUNCH, DINNER">Lunch, Dinner</SelectItem>
                                    <SelectItem value="BREAKFAST, LUNCH, DINNER">Breakfast, Lunch, Dinner</SelectItem>
                                    <SelectItem value="ALL MEALS">All Meals Included</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            {/* Activities */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label>Activities</Label>
                                <Button type="button" variant="ghost" size="sm" onClick={() => addActivity(dayIndex)}>
                                  <Plus className="w-3 h-3 mr-1" /> Activity
                                </Button>
                              </div>
                              {day.activities.map((act, actIndex) => (
                                <div key={actIndex} className="grid grid-cols-12 gap-2 items-start bg-slate-50 p-2 rounded">
                                  <Input
                                    className="col-span-2"
                                    placeholder="Time"
                                    value={act.time}
                                    onChange={(e) => updateActivity(dayIndex, actIndex, 'time', e.target.value)}
                                  />
                                  <Input
                                    className="col-span-3"
                                    placeholder="Activity"
                                    value={act.activity}
                                    onChange={(e) => updateActivity(dayIndex, actIndex, 'activity', e.target.value)}
                                  />
                                  <Input
                                    className="col-span-4"
                                    placeholder="Description"
                                    value={act.description}
                                    onChange={(e) => updateActivity(dayIndex, actIndex, 'description', e.target.value)}
                                  />
                                  <Input
                                    className="col-span-2"
                                    placeholder="Location"
                                    value={act.location}
                                    onChange={(e) => updateActivity(dayIndex, actIndex, 'location', e.target.value)}
                                  />
                                  <Button type="button" variant="ghost" size="sm" onClick={() => removeActivity(dayIndex, actIndex)} className="col-span-1">
                                    <X className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                            
                            <Button type="button" variant="destructive" size="sm" onClick={() => removeDay(dayIndex)}>
                              <Trash2 className="w-4 h-4 mr-1" /> Remove Day
                            </Button>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </div>
              )}

              {/* Inclusions & Exclusions */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="font-semibold" style={{ color: '#383838' }}>Inclusions</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newInclusion}
                      onChange={(e) => setNewInclusion(e.target.value)}
                      placeholder="e.g., Breakfast included"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addInclusion())}
                    />
                    <Button type="button" variant="outline" onClick={addInclusion}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.inclusions.map((inc, i) => (
                      <Badge key={i} variant="secondary" className="bg-green-100 text-green-700">
                        {inc}
                        <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => removeInclusion(i)} />
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <Label className="font-semibold text-red-600">Exclusions</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newExclusion}
                      onChange={(e) => setNewExclusion(e.target.value)}
                      placeholder="e.g., Airfare"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExclusion())}
                    />
                    <Button type="button" variant="outline" onClick={addExclusion}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.exclusions.map((exc, i) => (
                      <Badge key={i} variant="secondary" className="bg-red-100 text-red-700">
                        {exc}
                        <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => removeExclusion(i)} />
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="space-y-3">
                <Label className="font-semibold">Terms & Conditions</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTerm}
                    onChange={(e) => setNewTerm(e.target.value)}
                    placeholder="e.g., 50% advance required"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTerm())}
                  />
                  <Button type="button" variant="outline" onClick={addTerm}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.terms.map((term, i) => (
                    <Badge key={i} variant="outline">
                      {term}
                      <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => removeTerm(i)} />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Text Preview (for text type) */}
              {itineraryType === 'text' && formData.destination && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold" style={{ color: '#383838' }}>WhatsApp Preview</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(generateTextItinerary())}
                    >
                      {copied ? <Check className="w-4 h-4 mr-1 text-green-600" /> : <Copy className="w-4 h-4 mr-1" />}
                      {copied ? 'Copied!' : 'Copy to Clipboard'}
                    </Button>
                  </div>
                  <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-sm whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">
                    {generateTextItinerary()}
                  </pre>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any special requirements or notes..."
                  rows={3}
                />
              </div>

              {/* Form Actions */}
              <div className="flex justify-between items-center pt-4 border-t">
                <div>
                  {(itineraryType === 'daywise' || itineraryType === 'template') && formData.days.length > 0 && (
                    <Button type="button" variant="outline" onClick={handleSaveAsTemplate}>
                      <Bookmark className="w-4 h-4 mr-2" />
                      Save as Template
                    </Button>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="text-white" style={{ backgroundColor: '#383838' }} disabled={submitting}>
                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create Itinerary
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Itineraries</TabsTrigger>
          <TabsTrigger value="text">Text</TabsTrigger>
          <TabsTrigger value="daywise">Day-by-Day</TabsTrigger>
          <TabsTrigger value="template">From Template</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Itineraries Table */}
      <Card>
        <CardContent className="p-0">
          {itineraries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <FileText className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Itineraries Yet</h3>
              <p className="text-muted-foreground mb-4">Create your first tour itinerary</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Travel Date</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itineraries
                  .filter(it => activeTab === 'all' || it.itinerary_type === activeTab)
                  .map((it) => (
                    <TableRow key={it.id}>
                      <TableCell className="font-medium">{it.title}</TableCell>
                      <TableCell>{getTypeBadge(it.itinerary_type)}</TableCell>
                      <TableCell>{getCustomerName(it.customer_id)}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          {it.destination}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {formatDate(it.travel_date)}
                        </span>
                      </TableCell>
                      <TableCell>{it.days?.length || '-'}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handlePreview(it)}>
                              <Eye className="w-4 h-4 mr-2" /> Preview
                            </DropdownMenuItem>
                            {it.itinerary_type === 'text' || it.text_content ? (
                              <DropdownMenuItem onClick={() => copyToClipboard(it.text_content || generateTextItinerary(it))}>
                                <Copy className="w-4 h-4 mr-2" /> Copy for WhatsApp
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDownloadPdf(it.id, it.destination)}>
                              <FileText className="w-4 h-4 mr-2" /> Download PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadDocx(it.id, it.destination)}>
                              <FileType className="w-4 h-4 mr-2" /> Download Word
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(it.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit" style={{ color: '#84bd0e' }}>
              {previewItinerary?.title}
            </DialogTitle>
          </DialogHeader>
          {previewItinerary && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {previewItinerary.destination}</span>
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {formatDate(previewItinerary.travel_date)}</span>
                <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {previewItinerary.travelers} travelers</span>
              </div>
              
              {previewItinerary.itinerary_type === 'text' || previewItinerary.text_content ? (
                <div className="space-y-3">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(previewItinerary.text_content || generateTextItinerary(previewItinerary))}
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copy for WhatsApp
                    </Button>
                  </div>
                  <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-sm whitespace-pre-wrap font-mono">
                    {previewItinerary.text_content || generateTextItinerary(previewItinerary)}
                  </pre>
                </div>
              ) : (
                <>
                  {previewItinerary.days?.map((day, i) => (
                    <Card key={i}>
                      <CardHeader className="py-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Badge style={{ backgroundColor: '#84bd0e' }} className="text-white">Day {day.day_number}</Badge>
                          {day.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="py-2">
                        {day.activities?.map((act, j) => (
                          <div key={j} className="flex items-start gap-3 py-2 border-b last:border-0">
                            <span className="text-sm font-medium text-muted-foreground w-16">{act.time || '-'}</span>
                            <div>
                              <div className="font-medium">{act.activity}</div>
                              {act.description && <div className="text-sm text-muted-foreground">{act.description}</div>}
                              {act.location && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {act.location}</div>}
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                  
                  {previewItinerary.inclusions?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-green-700 mb-2">Inclusions</h4>
                      <div className="flex flex-wrap gap-2">
                        {previewItinerary.inclusions.map((inc, i) => (
                          <Badge key={i} variant="secondary" className="bg-green-100 text-green-700">{inc}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {previewItinerary.exclusions?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-red-700 mb-2">Exclusions</h4>
                      <div className="flex flex-wrap gap-2">
                        {previewItinerary.exclusions.map((exc, i) => (
                          <Badge key={i} variant="secondary" className="bg-red-100 text-red-700">{exc}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
